import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../supabase';
import {
  completePasswordReset,
  getDashboardPath,
  isPasswordRecoveryPending,
  requestStaffPasswordReset,
} from '../../services/auth/authApi';
import AuthHexBackground from './AuthHexBackground';
import PasswordField from './PasswordField';
import './Auth.css';

const STAFF_ROLES = ['admin', 'receptionist'];

function Login() {
  const { login, loading, isAuthenticated, account, authNotice, clearAuthNotice } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: '', password: '' });
  const [resetForm, setResetForm] = useState({ newPassword: '', confirmPassword: '' });
  const [mode, setMode] = useState(() => (isPasswordRecoveryPending() ? 'recovery' : 'signin'));
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const from = location.state?.from;

  useEffect(() => {
    if (isPasswordRecoveryPending()) {
      setMode('recovery');
    }
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setMode('recovery');
      }
    });
    return () => data.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (authNotice) setError(authNotice);
    else if (location.state?.notice) setError(location.state.notice);
  }, [authNotice, location.state?.notice]);

  // Only auto-redirect matching roles, and never while a login attempt or password reset is in progress
  if (
    !loading
    && !submitting
    && mode !== 'recovery'
    && !isPasswordRecoveryPending()
    && isAuthenticated
    && STAFF_ROLES.includes(account?.role)
  ) {
    const target = from && !String(from).startsWith('/owner')
      ? from
      : getDashboardPath(account.role);
    return <Navigate to={target} replace />;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setError('');
    setSuccess('');
    clearAuthNotice();
  };

  const handleResetChange = (e) => {
    const { name, value } = e.target;
    setResetForm((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const result = await login(form.email, form.password, { allowedRoles: STAFF_ROLES });
      const role = result.account?.role;
      const target = from && !String(from).startsWith('/owner')
        ? from
        : getDashboardPath(role);
      navigate(target, { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed. Check your email and password.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      await requestStaffPasswordReset(form.email);
      setSuccess('If that email has a login, a reset link is on its way. Check your inbox.');
    } catch (err) {
      setError(err.message || 'Could not send the reset email.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecoverySubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await completePasswordReset({
        newPassword: resetForm.newPassword,
        confirmPassword: resetForm.confirmPassword,
      });
      setResetForm({ newPassword: '', confirmPassword: '' });
      if (account && STAFF_ROLES.includes(account.role)) {
        navigate(getDashboardPath(account.role), { replace: true });
        return;
      }
      setMode('signin');
      setSuccess('Password updated. Sign in with your new password.');
    } catch (err) {
      setError(err.message || 'Could not update password.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="auth-loading">
        <AuthHexBackground />
        <div className="auth-loading-spinner" aria-hidden="true" />
        <p>Loading…</p>
      </div>
    );
  }

  const brandTitle = mode === 'forgot'
    ? 'Forgot password'
    : mode === 'recovery'
      ? 'Set new password'
      : 'Gym CRM';
  const brandSubtitle = mode === 'forgot'
    ? 'Enter the admin or receptionist email for this gym'
    : mode === 'recovery'
      ? 'Choose a new password for your admin or receptionist login'
      : 'Sign in as Gym Admin or Receptionist';

  return (
    <div className="auth-page">
      <AuthHexBackground />
      <div className="auth-card auth-card-neon">
        <div className="auth-brand">
          <h1>{brandTitle}</h1>
          <p>{brandSubtitle}</p>
        </div>

        {error && <p className="auth-error" role="alert">{error}</p>}
        {success && <p className="auth-success" role="status">{success}</p>}

        {mode === 'signin' && (
          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="owner-form-group">
              <label className="owner-form-label" htmlFor="login-email">Email</label>
              <input
                id="login-email"
                name="email"
                type="email"
                className="owner-form-input"
                value={form.email}
                onChange={handleChange}
                autoComplete="email"
                required
              />
            </div>
            <PasswordField
              id="login-password"
              name="password"
              value={form.password}
              onChange={handleChange}
              autoComplete="current-password"
              required
            />
            <div className="auth-forgot-row">
              <button
                type="button"
                className="auth-link auth-forgot-btn"
                onClick={() => {
                  setError('');
                  setSuccess('');
                  setMode('forgot');
                }}
              >
                Forgot password?
              </button>
            </div>
            <button type="submit" className="auth-submit" disabled={submitting}>
              {submitting ? 'Signing in…' : 'Sign In'}
            </button>
          </form>
        )}

        {mode === 'forgot' && (
          <form className="auth-form" onSubmit={handleForgotSubmit}>
            <div className="owner-form-group">
              <label className="owner-form-label" htmlFor="login-email">Email</label>
              <input
                id="login-email"
                name="email"
                type="email"
                className="owner-form-input"
                value={form.email}
                onChange={handleChange}
                autoComplete="email"
                required
              />
            </div>
            <button type="submit" className="auth-submit" disabled={submitting}>
              {submitting ? 'Sending…' : 'Send reset link'}
            </button>
          </form>
        )}

        {mode === 'recovery' && (
          <form className="auth-form" onSubmit={handleRecoverySubmit}>
            <PasswordField
              id="reset-new-password"
              name="newPassword"
              label="New password"
              value={resetForm.newPassword}
              onChange={handleResetChange}
              autoComplete="new-password"
              minLength={6}
              required
            />
            <PasswordField
              id="reset-confirm-password"
              name="confirmPassword"
              label="Confirm new password"
              value={resetForm.confirmPassword}
              onChange={handleResetChange}
              autoComplete="new-password"
              minLength={6}
              required
            />
            <button type="submit" className="auth-submit" disabled={submitting}>
              {submitting ? 'Updating…' : 'Update password'}
            </button>
          </form>
        )}

        <p className="auth-footer">
          {mode === 'signin' ? (
            <>
              New gym?{' '}
              <Link to="/register">Register now</Link>
              {' '}— choose a plan or start a free trial.
            </>
          ) : (
            <button
              type="button"
              className="auth-link auth-forgot-btn"
              onClick={() => {
                setError('');
                setSuccess('');
                setMode('signin');
              }}
            >
              Back to sign in
            </button>
          )}
        </p>
      </div>
    </div>
  );
}

export default Login;
